import { useState, useEffect, useCallback } from 'react';
import { PlayerAvatar, invalidateAvatarCache } from './PlayerAvatar';
import AvailabilityProfile from './AvailabilityProfile';
import SocialListsSettings from './SocialListsSettings';
import AvatarEditor from './AvatarEditor';
import { CameraIcon, EditIcon, SaveIcon } from './Icons';
import { useApp } from '../contexts/AppContext';
import type { Session } from '../types/app';
import { BACKEND_URL } from '../config';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ProfileData = Record<string, any>;

interface ProfileSettingsProps {
  session: Session;
}

const EDITABLE_FIELDS: { key: string; label: string; type: string; placeholder?: string }[] = [
  { key: 'ville', label: 'Ville', type: 'text', placeholder: 'Paris' },
  { key: 'pays', label: 'Pays', type: 'text', placeholder: 'France' },
  { key: 'profession', label: 'Profession', type: 'text', placeholder: '' },
];

function fmtDate(v: unknown): string {
  if (!v) return '';
  try {
    return new Date(String(v)).toLocaleDateString('fr-FR');
  } catch {
    return String(v);
  }
}

export default function ProfileSettings({ session }: ProfileSettingsProps) {
  const { avatarVersion, bumpAvatarVersion } = useApp();
  const [profile, setProfile] = useState<ProfileData | null>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editValues, setEditValues] = useState<Record<string, string>>({});
  const [saveMessage, setSaveMessage] = useState<string | null>(null);
  const [showAvatarEditor, setShowAvatarEditor] = useState(false);
  const [avatarKey, setAvatarKey] = useState(avatarVersion);
  const [publiContacts, setPubliContacts] = useState(true);

  const fetchProfile = useCallback(async () => {
    try {
      const url = `${BACKEND_URL}/api/profiles/player/${encodeURIComponent(session.pseudo)}`;
      const res = await fetch(url, {
        headers: session.token ? { Authorization: `Bearer ${session.token}` } : {},
      });
      const json = await res.json();
      if (json.success && json.data) {
        setProfile(json.data);
        setEditValues({
          ville: json.data.ville || '',
          pays: json.data.pays || '',
          profession: json.data.classic?.personal?.profession || '',
          commentaire: json.data.classic?.personal?.commentaire || '',
        });
        setPubliContacts(json.data.contacts?.publi_contacts !== 'false');
      }
    } catch (err) {
      console.error('[ProfileSettings] Load error:', err);
    } finally {
      setLoading(false);
    }
  }, [session.pseudo, session.token]);

  useEffect(() => { fetchProfile(); }, [fetchProfile]);

  const handleSave = async () => {
    setSaving(true);
    setSaveMessage(null);
    try {
      const body: Record<string, unknown> = {};
      if (editValues.ville !== (profile?.ville || '')) body.ville = editValues.ville || null;
      if (editValues.pays !== (profile?.pays || '')) body.pays = editValues.pays || null;
      if (editValues.profession !== (profile?.classic?.personal?.profession || '')) body.profession = editValues.profession || null;
      if (editValues.commentaire !== (profile?.classic?.personal?.commentaire || '')) body.commentaire = editValues.commentaire || null;

      if (Object.keys(body).length === 0) {
        setEditing(false);
        return;
      }

      const res = await fetch(
        `${BACKEND_URL}/api/profiles/player/${encodeURIComponent(session.pseudo)}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            ...(session.token ? { Authorization: `Bearer ${session.token}` } : {}),
          },
          body: JSON.stringify(body),
        }
      );

      if (res.ok) {
        setSaveMessage('Profil mis à jour');
        setEditing(false);
        fetchProfile();
        setTimeout(() => setSaveMessage(null), 3000);
      } else {
        setSaveMessage('Erreur lors de la sauvegarde');
        setTimeout(() => setSaveMessage(null), 4000);
      }
    } catch {
      setSaveMessage('Erreur réseau');
      setTimeout(() => setSaveMessage(null), 4000);
    } finally {
      setSaving(false);
    }
  };

  const handleTogglePubliContacts = async () => {
    const newVal = !publiContacts;
    setPubliContacts(newVal);
    try {
      await fetch(
        `${BACKEND_URL}/api/profiles/player/${encodeURIComponent(session.pseudo)}/preferences`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            ...(session.token ? { Authorization: `Bearer ${session.token}` } : {}),
          },
          body: JSON.stringify({ publi_contacts: newVal ? 'true' : 'false' }),
        }
      );
    } catch {
      setPubliContacts(!newVal);
    }
  };

  const handleAvatarSaved = () => {
    invalidateAvatarCache(session.pseudo);
    bumpAvatarVersion();
    setAvatarKey(k => k + 1);
    setShowAvatarEditor(false);
    fetchProfile();
  };

  if (loading) {
    return <div className="profile-settings-loading">Chargement du profil...</div>;
  }

  if (!profile) {
    return <div className="profile-settings-error">Impossible de charger le profil</div>;
  }

  const isRegistered = profile.registered === 'oui';
  const isEvaluation = profile.registered === 'évaluation';
  const hasCompetition = !!(session.hasCompetitionAccess || session.compet);
  const subscription = profile.competition_subscription;

  return (
    <section className="settings-section profile-settings">
      {/* Avatar + Identity header */}
      <div className="profile-settings-header">
        <div className="profile-settings-avatar-wrapper">
          <PlayerAvatar
            key={avatarKey}
            pseudo={session.pseudo}
            size={80}
            cacheBust={avatarKey || undefined}
            photoUrl={avatarKey > 0 ? `${BACKEND_URL}/api/avatars/${encodeURIComponent(session.pseudo)}?size=160&t=${avatarKey}` : undefined}
          />
          <button
            className="profile-settings-avatar-edit"
            onClick={() => setShowAvatarEditor(true)}
            title="Modifier l'avatar"
          >
            <CameraIcon size={16} />
          </button>
        </div>
        <div className="profile-settings-identity">
          <div className="profile-settings-pseudo">{profile.pseudo_display || session.pseudo}</div>
          <div className="profile-settings-access-list">
            <div className={`profile-access-item ${isRegistered || isEvaluation ? 'granted' : 'denied'}`}>
              <span className="profile-access-icon">{isRegistered || isEvaluation ? '✓' : '✗'}</span>
              <span>Détente{isEvaluation ? ' (évaluation)' : ''}</span>
            </div>
            <div className={`profile-access-item ${hasCompetition ? 'granted' : 'denied'}`}>
              <span className="profile-access-icon">{hasCompetition ? '✓' : '✗'}</span>
              <span>Compétition</span>
            </div>
          </div>
          {subscription?.fin_abonnement && (
            <div className="profile-settings-subscription">
              Abonnement jusqu'au {fmtDate(subscription.fin_abonnement)}
            </div>
          )}
        </div>
      </div>

      {/* Read-only info */}
      <div className="profile-settings-section">
        <div className="profile-settings-section-title">Identité</div>
        <div className="profile-settings-grid">
          {profile.prenom && (
            <div className="profile-settings-field">
              <span className="profile-settings-field-label">Prénom</span>
              <span className="profile-settings-field-value">{profile.prenom}</span>
            </div>
          )}
          {profile.nom && (
            <div className="profile-settings-field">
              <span className="profile-settings-field-label">Nom</span>
              <span className="profile-settings-field-value">{profile.nom}</span>
            </div>
          )}
          {profile.sexe && (
            <div className="profile-settings-field">
              <span className="profile-settings-field-label">Sexe</span>
              <span className="profile-settings-field-value">
                {profile.sexe === 'M' ? 'Homme' : profile.sexe === 'F' ? 'Femme' : profile.sexe}
              </span>
            </div>
          )}
          {profile.licence && (
            <div className="profile-settings-field">
              <span className="profile-settings-field-label">Licence FFT</span>
              <span className="profile-settings-field-value">{profile.licence}</span>
            </div>
          )}
        </div>
      </div>

      {/* Editable fields */}
      <div className="profile-settings-section">
        <div className="profile-settings-section-header">
          <div className="profile-settings-section-title">Informations personnelles</div>
          {!editing ? (
            <button className="profile-settings-edit-btn" onClick={() => setEditing(true)}>
              <EditIcon size={14} />
              <span>Modifier</span>
            </button>
          ) : (
            <div className="profile-settings-edit-actions">
              <button
                className="profile-settings-cancel-btn"
                onClick={() => {
                  setEditing(false);
                  setEditValues({
                    ville: profile.ville || '',
                    pays: profile.pays || '',
                    profession: profile.classic?.personal?.profession || '',
                    commentaire: profile.classic?.personal?.commentaire || '',
                  });
                }}
              >
                Annuler
              </button>
              <button
                className="profile-settings-save-btn"
                onClick={handleSave}
                disabled={saving}
              >
                <SaveIcon size={14} />
                <span>{saving ? 'Enregistrement...' : 'Enregistrer'}</span>
              </button>
            </div>
          )}
        </div>

        {saveMessage && (
          <div className={`profile-settings-message ${saveMessage.includes('Erreur') ? 'error' : 'success'}`}>
            {saveMessage}
          </div>
        )}

        <div className="profile-settings-grid">
          {EDITABLE_FIELDS.map(field => (
            <div key={field.key} className="profile-settings-field">
              <span className="profile-settings-field-label">{field.label}</span>
              {editing ? (
                <input
                  type={field.type}
                  className="profile-settings-input"
                  value={editValues[field.key] || ''}
                  placeholder={field.placeholder}
                  onChange={e => setEditValues(prev => ({ ...prev, [field.key]: e.target.value }))}
                />
              ) : (
                <span className="profile-settings-field-value">
                  {editValues[field.key] || <span className="profile-settings-empty">—</span>}
                </span>
              )}
            </div>
          ))}
          <div className="profile-settings-field profile-settings-field-full">
            <span className="profile-settings-field-label">Commentaire</span>
            {editing ? (
              <textarea
                className="profile-settings-textarea"
                value={editValues.commentaire || ''}
                rows={3}
                onChange={e => setEditValues(prev => ({ ...prev, commentaire: e.target.value }))}
              />
            ) : (
              <span className="profile-settings-field-value">
                {editValues.commentaire || <span className="profile-settings-empty">—</span>}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Privacy / Confidentialité */}
      <div className="profile-settings-section">
        <div className="profile-settings-section-title">Confidentialité</div>
        <label className="profile-settings-privacy-row">
          <div className="profile-settings-privacy-info">
            <span className="profile-settings-privacy-label">Contacts visibles</span>
            <span className="profile-settings-privacy-desc">
              Rendre votre email de contact public accessible aux autres joueurs (sinon visible uniquement par votre équipe).
              Pour modifier l'email de connexion / récupération, rendez-vous dans « Compte &amp; sécurité ».
            </span>
          </div>
          <div className="settings-toggle-switch-container">
            <input
              type="checkbox"
              className="settings-toggle-input"
              checked={publiContacts}
              onChange={handleTogglePubliContacts}
            />
            <span className="settings-toggle-switch" />
          </div>
        </label>
      </div>

      {/* Availability */}
      <div className="profile-settings-section">
        <div className="profile-settings-section-title">Disponibilité pour jouer</div>
        <p className="profile-settings-section-desc">
          Définissez comment vous souhaitez recevoir les invitations à jouer des autres joueurs.
        </p>
        <AvailabilityProfile pseudo={session.pseudo} token={session.token} />
      </div>

      {/* Listes sociales */}
      <div className="profile-settings-section">
        <div className="profile-settings-section-title">Amis et joueurs ignorés</div>
        <p className="profile-settings-section-desc">
          Gérez vos listes personnelles. Les coéquipiers sont détectés automatiquement via votre équipe.
        </p>
        <SocialListsSettings
          pseudo={session.pseudo}
          token={session.token}
          compet={session.compet}
        />
      </div>

      {/* Avatar Editor Modal */}
      {showAvatarEditor && (
        <AvatarEditor
          pseudo={session.pseudo}
          token={session.token || ''}
          onClose={() => setShowAvatarEditor(false)}
          onSaved={handleAvatarSaved}
        />
      )}
    </section>
  );
}
